/**
 * AuthContext - Authentication state management
 * Provides login/logout functionality with demo user support
 */
import React, { createContext, useContext, useState, useCallback } from 'react';

// Demo user for testing
const DEMO_USER = {
  id: 'demo-user-001',
  name: 'Sarah Johnson',
  email: 'sarah.johnson@email.com',
  avatar: '👩',
  memberSince: 'January 2024',
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children, startLoggedIn = true }) => {
  // Start logged in with demo user by default
  const [user, setUser] = useState(startLoggedIn ? DEMO_USER : null);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  
  const isLoggedIn = !!user;

  const login = useCallback((email, password) => {
    // Demo login - always succeeds
    // In production, this would call an API
    setUser(DEMO_USER);
    setIsLoginModalOpen(false);
    return { success: true };
  }, []);

  const loginWithDemo = useCallback(() => {
    setUser(DEMO_USER);
    setIsLoginModalOpen(false);
  }, []);

  const logout = useCallback(() => {
    setUser(null);
  }, []);

  const signup = useCallback((name, email, password) => {
    // Demo signup - creates user with provided info
    setUser({
      ...DEMO_USER,
      name,
      email,
      memberSince: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
    });
    setIsLoginModalOpen(false);
    return { success: true };
  }, []);

  const openLoginModal = useCallback(() => {
    setIsLoginModalOpen(true);
  }, []);

  const closeLoginModal = useCallback(() => {
    setIsLoginModalOpen(false);
  }, []);

  const value = {
    user,
    isLoggedIn,
    login,
    loginWithDemo,
    logout,
    signup,
    isLoginModalOpen,
    openLoginModal,
    closeLoginModal,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
